import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'

export interface KnowledgeFolderItem {
  id: string
  name: string
  parent: string | null
  description: string
  path: string
}

export interface KnowledgeFolderInput {
  name?: string
  parent?: string | null
  description?: string
}

const BASE = '/api/v1/insure/internal/knowledge-folders'

function listFolders(): Promise<KnowledgeFolderItem[]> {
  return axios.get(`${BASE}/`).then((r) => {
    const data = toCamelCase(r.data)
    const results = (data as { results?: KnowledgeFolderItem[] }).results
    return (results ?? (data as KnowledgeFolderItem[])) as KnowledgeFolderItem[]
  })
}

function createFolder(data: KnowledgeFolderInput): Promise<KnowledgeFolderItem> {
  return axios.post(`${BASE}/`, toSnakeCase(data)).then((r) => toCamelCase(r.data) as KnowledgeFolderItem)
}

function updateFolder(id: string, data: KnowledgeFolderInput): Promise<KnowledgeFolderItem> {
  return axios
    .patch(`${BASE}/${id}/`, toSnakeCase(data))
    .then((r) => toCamelCase(r.data) as KnowledgeFolderItem)
}

function deleteFolder(id: string): Promise<void> {
  return axios.delete(`${BASE}/${id}/`).then(() => undefined)
}

export function useKnowledgeFolders() {
  return useQuery({
    queryKey: [CacheKeys.KNOWLEDGE_FOLDERS],
    queryFn: listFolders,
  })
}

// Deleting a folder re-parents its documents, so invalidate both caches.
function useFolderMutation<TVariables, TData>(mutationFn: (variables: TVariables) => Promise<TData>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_FOLDERS] })
      qc.invalidateQueries({ queryKey: [CacheKeys.KNOWLEDGE_DOCUMENTS] })
    },
  })
}

export function useCreateKnowledgeFolder() {
  return useFolderMutation(createFolder)
}

export function useUpdateKnowledgeFolder() {
  return useFolderMutation(({ id, ...data }: { id: string } & KnowledgeFolderInput) => updateFolder(id, data))
}

export function useDeleteKnowledgeFolder() {
  return useFolderMutation(deleteFolder)
}

export { ancestorIds, collectSubtreeIds, groupFoldersByParent } from '../lib/folderTree'
